/**
 * The advisor's agency name, for the two places it is printed.
 *
 * Legacy applies it by sweeping the DOM after a save — `applyBranding`
 * (app.html:8844-8850) sets `.brand-name`, `.dash-header .brand` and
 * `.editor-nav .brand` by hand. That works in a page that is built once and
 * mutated forever. It does not work here: every screen is repainted from
 * state, so a swept-in name is wiped by the next repaint and the wordmark
 * silently reverts to "MBM Studio".
 *
 * So it is held here instead and READ at render time. One module-level value,
 * like `theme-store` and `split-store`: `main.js` sets it on sign-in, the
 * Settings modal sets it as you type, and every view that draws a wordmark
 * asks for it.
 *
 * ── Truncated at twenty ───────────────────────────────────────────────────
 *
 * Legacy's own cap (8848: `agencyName.length > 20 ? slice(0,20)`), and it is
 * the right one — the wordmark is a 58px serif on the dashboard and a 24px
 * one in the editor bar, and a fifty-character agency name in either wraps the
 * header onto two lines and pushes the trip cards down the page. The full name
 * is kept; only the wordmark is shortened.
 */

/** What the app is called when nobody has said otherwise. */
export const DEFAULT_WORDMARK = 'MBM Studio'

let name = ''

/**
 * Set it. Anything unusable clears it, so the app falls back to its own name
 * rather than printing "undefined" across the top of the dashboard.
 *
 * @param {unknown} value
 * @returns {string} what was stored
 */
export function setAgencyName(value) {
  name = String(value == null ? '' : value).trim()
  return name
}

/** The full name, or '' when none is set. @returns {string} */
export function agencyName() {
  return name
}

let phone = ''

/**
 * The number printed on a client packet's closing page.
 *
 * Held here beside the name because they are the same kind of thing — an
 * agency's identity as a client sees it — and because everything that prints a
 * packet already imports this module for `agencyName`.
 *
 * @param {unknown} value @returns {string} what was stored
 */
export function setAgencyPhone(value) {
  phone = String(value == null ? '' : value).trim()
  return phone
}

/** The contact number, or '' when none is set. @returns {string} */
export function agencyPhone() {
  return phone
}

/**
 * The wordmark at the top left of every screen.
 *
 * ── It is ALWAYS the product's name ───────────────────────────────────────
 *
 * This returned the agency's name for one afternoon, because that is what
 * legacy does — `applyBranding` overwrites the dashboard and editor wordmarks
 * with `mbm_brandAgencyName` (app.html:8844-8850). Trey, 20 Aug 2026:
 * "I still kinda what MBM Studio branding to be on the dashboard and at the
 * top left of all screens... i want my program to remain branded by me."
 *
 * He is right, and legacy is wrong, because there are two brands here and
 * they are not the same kind of thing:
 *
 *   MBM Studio is the SOFTWARE. The chrome is its shop sign, and an advisor
 *   using it is a customer rather than the owner.
 *
 *   The agency is the advisor's BUSINESS. What their client receives — the
 *   itinerary, the enquiry form, anything that leaves the building — carries
 *   the agency's name and logo, because that is their client relationship and
 *   not the product's.
 *
 * So the split is: `agencyName()` and `logoUrl()` for documents,
 * `wordmark()` for chrome, and this function takes no argument for a reason.
 * If a per-agency override is ever wanted, it belongs behind a setting the
 * PRODUCT owner turns on, not behind whatever an advisor typed in a box.
 *
 * @returns {string}
 */
export function wordmark() {
  return DEFAULT_WORDMARK
}

/* ── the logo ─────────────────────────────────────────────────────────────
   Held here for the same reason the name is: it is not a CSS custom property,
   so `applyBrand` cannot deliver it, and the screens and documents that draw
   it are repainted from state and would lose anything swept in.

   The ORIGINAL stores a logo and draws it nowhere — `mbm_brandLogoUrl` is
   read back into the settings box (app.html:8789) and written (8812) and that
   is the whole of it. Trey, 20 Aug 2026: "it should accept any format and
   apply it to the necessary locations throughout the program and the documents
   that the agents create and send or give to their clients." */

let logo = ''
let onSocial = true

/**
 * Set the logo. Anything that is not a usable image source clears it, so a
 * broken value produces no `<img>` rather than a broken one on a document a
 * client opens.
 *
 * @param {unknown} value
 * @returns {string} what was stored
 */
export function setLogo(value) {
  const raw = String(value == null ? '' : value).trim()
  logo = isUsableImageSrc(raw) ? raw : ''
  return logo
}

/** The logo, or '' when there is none. @returns {string} */
export function logoUrl() {
  return logo
}

/**
 * Whether the logo may be used on social exports.
 *
 * A separate switch because it is a separate decision. A logo belongs on an
 * itinerary a client opens; on a square posted to Instagram an advisor may
 * want the image to speak for itself, and legacy's own social export composes
 * the agency NAME and three brand colours and deliberately carries no logo
 * (`readBrandForSocial`, app.html:9407).
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function setLogoOnSocial(value) {
  onSocial = value !== false && value !== 'false' && value !== 0 && value !== '0'
  return onSocial
}

/** @returns {boolean} */
export function logoOnSocial() {
  return onSocial
}

/** The logo to draw on a social export: the logo, or '' when it is switched off. */
export function socialLogoUrl() {
  return onSocial ? logo : ''
}

/**
 * Is this something an `<img src>` can safely be pointed at?
 *
 * `https:` and `data:image/` only. Not `http:` — these URLs end up in
 * documents a client opens and in a public intake page, and a mixed-content
 * image is blocked by the browser anyway, so allowing it only produces a
 * silent blank. Explicitly NOT `javascript:` or anything else with a scheme.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isUsableImageSrc(value) {
  const raw = String(value == null ? '' : value).trim()
  if (!raw) return false
  if (/^data:image\/[a-z0-9.+-]+[;,]/i.test(raw)) return true
  return /^https:\/\/[^\s]+$/i.test(raw)
}
